import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    effect,
    inject,
    input,
    output,
    untracked,
    viewChild,
    ChangeDetectionStrategy,
} from "@angular/core"
import {DevicePixelRatioService} from "@app/common/services/device-pixel-ratio/device-pixel-ratio.service"
import {ThreeMaterialManagerService} from "@app/template-editor/services/three-material-manager.service"
import {ThreeSceneManagerService} from "@app/template-editor/services/three-scene-manager.service"
import {ThreeTextureTilesManagerService} from "@template-editor/services/three-texture-tiles-manager.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-three-template-scene-provider",
    templateUrl: "./three-template-scene-provider.component.html",
    styleUrl: "./three-template-scene-provider.component.scss",
    providers: [ThreeSceneManagerService, ThreeMaterialManagerService, ThreeTextureTilesManagerService],
})
export class ThreeTemplateSceneProviderComponent implements OnInit, OnDestroy, AfterViewInit {
    private readonly $canvas = viewChild.required<ElementRef<HTMLCanvasElement>>("canvas")
    private readonly $canvasContainer = viewChild.required<ElementRef<HTMLDivElement>>("canvasContainer")
    private resizeObserver: ResizeObserver

    readonly onInitialized = output<ThreeSceneManagerService | undefined>()

    private readonly localThreeSceneManagerService = inject(ThreeSceneManagerService)
    private readonly localThreeMaterialManagerService = inject(ThreeMaterialManagerService)
    private readonly dprService = inject(DevicePixelRatioService)
    readonly $pixelRatio = input<number>(undefined, {alias: "pixelRatio"})
    readonly $forceWebGl = input(false, {alias: "forceWebGl"})

    constructor() {
        this.localThreeMaterialManagerService.setThreeSceneManagerService(this.localThreeSceneManagerService)
        this.resizeObserver = new ResizeObserver(() => {
            this.resizeRenderer()
        })

        effect(() => {
            this.localThreeSceneManagerService.$renderer()
            this.dprService.$devicePixelRatio()
            this.$pixelRatio()
            untracked(() => this.resizeRenderer())
        })
    }

    ngOnInit() {
        if (!this.$canvas) throw new Error("Canvas not found")
        this.localThreeSceneManagerService.$forceWebGl.set(this.$forceWebGl())
        this.localThreeSceneManagerService.init(this.$canvas().nativeElement)
        this.resizeRenderer()
        this.onInitialized.emit(this.localThreeSceneManagerService)
    }

    ngAfterViewInit() {
        this.resizeObserver.observe(this.$canvasContainer().nativeElement)
    }

    ngOnDestroy() {
        this.onInitialized.emit(undefined)
    }

    private resizeRenderer() {
        const parentElement = this.$canvas().nativeElement.parentElement
        if (!parentElement) throw new Error("No parent element for canvas found")

        const {width, height} = parentElement.getBoundingClientRect()
        this.localThreeSceneManagerService.resizeRenderer(Math.floor(width), Math.floor(height), (this.$pixelRatio() ?? 1) * this.dprService.getCurrent())
    }
}
